import React from "react";

import { IconButton, TooltipProps } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import LightTooltip from "./LightTooltip";

// Base interface
interface IconTooltipBaseProps {
  title: string; // Tooltip title
  placement?: TooltipProps["placement"]; // Tooltip placement (e.g., 'right', 'left', 'top', 'bottom')
  sx?: SxProps<Theme>; // Optional custom styles for the IconButton
}

type IconTooltipProps = IconTooltipBaseProps &
  (
    | { src: string; IconElement?: never } // src is provided, IconElement is not
    | { src?: never; IconElement: React.ReactNode } // IconElement is provided, src is not
  );

/**
 * An IconTooltip component that displays a tooltip when hovering over an icon or image.
 * The component can accept either an `src` prop for an image or an `IconElement` prop for a custom icon.
 * Not both and not none.
 *
 * @component
 * @param {IconTooltipProps} props - The props for the IconTooltip component.
 * @param {string} props.title - The text to display in the tooltip.
 * @param {TooltipProps["placement"]} [props.placement="right"] - The placement of the tooltip.
 * @param {string} [props.src] - The source URL for the image to display. If provided, `IconElement` must not be provided.
 * @param {React.ReactNode} [props.IconElement] - A custom icon element to display. If provided, `src` must not be provided.
 * @param {SxProps<Theme>} [props.sx] - Optional custom styles for the IconButton.
 * @returns {JSX.Element} The rendered IconTooltip component.
 */

const IconTooltip: React.FC<IconTooltipProps> = ({
  title,
  placement = "right",
  src,
  IconElement,
  sx
}) => {
  const sidebarIconStyle: SxProps<Theme> = {
    borderRadius: 0,
    width: "100%",
    "&:hover": {
      background: "none"
    }
  };

  return (
    <LightTooltip title={title} placement={placement} arrow>
      <IconButton
        aria-label={title}
        {...(sx ? { sx } : { sx: sidebarIconStyle })}
      >
        {src ? (
          <img src={src} alt={title} className='w-4' /> // Rendering img if src is provided
        ) : (
          IconElement // Rendering IconElement if it is provided
        )}
      </IconButton>
    </LightTooltip>
  );
};

export default IconTooltip;
